import React, { memo, useMemo } from 'react';

export interface FormSelectOption {
  value: string;
  label: string;
}

export interface FormSelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label: string;
  required?: boolean;
  backgroundColor?: 'primary' | 'secondary';
  options: FormSelectOption[];
  placeholder?: string;
}

export const FormSelect: React.FC<FormSelectProps> = memo(({
  label,
  required = false,
  backgroundColor = 'primary',
  options,
  placeholder,
  className = '',
  ...props
}) => {
  const bgColor = useMemo(() => backgroundColor === 'primary' 
    ? 'var(--color-bg-primary)' 
    : 'var(--color-bg-secondary)', [backgroundColor]);

  const labelText = useMemo(() => `${label}${required ? ' *' : ''}`, [label, required]);

  return (
    <div>
      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
        {labelText}
      </label>
      <div className="relative">
        <select
          {...props}
          required={required}
          className={`w-full pl-3 pr-12 py-2 border rounded-lg focus:ring-2 transition-colors appearance-none ${className}`}
          style={{
            backgroundColor: bgColor,
            borderColor: 'var(--color-border)',
            color: 'var(--color-text-primary)',
            '--tw-ring-color': 'var(--color-accent)',
            ...props.style
          } as React.CSSProperties}
        >
          {placeholder && (
            <option value="">{placeholder}</option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div 
          className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
          style={{ color: 'var(--color-text-primary)' }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
  );
});

FormSelect.displayName = 'FormSelect';

